<template>
  <confirm-dialog ref="confirmCeaseToTrade"></confirm-dialog>
  <div v-if="errorMessage" class="alert alert-danger">{{ errorMessage }}</div>
  <div class="row">
    <card v-if="model" label="Cease to Trade" class="col-xl-6 col-lg-6 col-md-12 me-2">
      <div class="row">
        <div class="col">
          <label class="form-label">Cease to Trade Date</label>
          <datepicker
            id="ceaseToTradeDate"
            v-model="model.ceaseToTradeDate"
            class="form-control"
            :style="{ background: isCeaseToTradeEditor ? 'white' : 'var(--bs-secondary-bg)' }"
            :disabled="!isCeaseToTradeEditor"
            :input-format="inputFormat"
          />
        </div>
        <div class="col">
          <smart-select
            id="tradingStatus"
            v-model="model.tradingStatus"
            label="Trading Status"
            :options="tradingStatusOptions"
            :disabled="!isCeaseToTradeEditor"
            :is-required="false"
          />
        </div>
      </div>

      <div class="row mt-3">
        <div class="col">
          <label class="form-label">Cease to Trade Evidence</label>
          <div
            v-if="model.ceaseToTradeEvidences && model.ceaseToTradeEvidences.length > 0"
            class="d-flex flex-column gap-2"
          >
            <div v-for="(evidence, index) in model.ceaseToTradeEvidences" :key="index">
              <scheme-business-file-upload
                :disabled="!isCeaseToTradeEditor"
                :model-value="evidence"
                label=""
                :show-file-input="false"
                @update:model-value="(file) => updateExistingFile(index, file)"
              />
            </div>
          </div>
          <div
            v-if="!model.ceaseToTradeEvidences || model.ceaseToTradeEvidences.length === 0"
            class="alert alert-warning"
          >
            No Evidence Uploaded
          </div>
        </div>
      </div>
      <scheme-business-file-upload
        v-if="isCeaseToTradeEditor"
        label="New Evidence"
        @update:model-value="addFileToModel"
      />

      <div class="d-flex items-center gap-2">
        <button
          v-if="isCeaseToTradeEditor"
          type="button"
          class="btn-primary btn float-left mb-2 mt-4"
          @click="saveCeaseToTrade"
        >
          Set Cease to Trade
        </button>
        <button
          v-if="isCeaseToTradeEditor && model.ceaseToTradeDate"
          type="button"
          class="btn-secondary btn float-left mb-2 mt-4"
          @click="enableBusiness"
        >
          Clear Cease to Trade
        </button>
      </div>

      <div v-if="model.ceaseToTradeActionedDate || model.ceaseToTradeActionedBy" class="row mt-3">
        <div class="col">
          <label class="form-label">Cease to Trade Actioned Date</label>
          <input
            class="form-control"
            :value="formatActionedDate(model.ceaseToTradeActionedDate)"
            disabled
          />
        </div>
        <div class="col">
          <label class="form-label">Cease to Trade Actioned By</label>
          <input class="form-control" :value="model.ceaseToTradeActionedBy || ''" disabled />
        </div>
      </div>
    </card>
    <Card
      v-if="companyInsolvency && companyInsolvency.cases.length > 0"
      class="col-xl-5 col-lg-5 col-md-12"
    >
      <h3 v-if="companyInsolvency">
        {{ companyInsolvency.cases.length }} Insolvency
        {{ companyInsolvency.cases.length === 1 ? 'case' : 'cases' }}
      </h3>
      <div
        v-if="companyInsolvency"
        v-for="(insolvency, index) in companyInsolvency.cases"
        :key="index"
      >
        <h3 class="mt-4">
          Case number {{ insolvency.number }} - {{ formatInsolvencyType(insolvency.type) }}
        </h3>
        <div class="row mt-3">
          <div class="col">
            <div v-if="insolvency.dates?.length > 0" class="d-flex gap-5">
              <div v-for="(insolvencyDate, index) in insolvency.dates" :key="index">
                <span>{{ formatDateLabel(insolvencyDate.type) }}</span>
                <p class="mt-2 fw-bolder">{{ formatInsolvencyDate(insolvencyDate.date) }}</p>
              </div>
            </div>
            <div
              class="row my-4"
              v-for="(practitioner, index) in insolvency.practitioners"
              :key="index"
            >
              <div class="col-6">
                <p>Practioner</p>
                <p class="fw-bolder">{{ practitioner.name }}</p>
                <p>{{ formatAddress(practitioner.address) }}</p>
              </div>
              <div class="col-6 d-flex gap-2">
                <div v-if="practitioner.appointed_on">
                  <span>Appointed on <br /></span>
                  <p class="fw-bolder">
                    {{ formatInsolvencyDate(practitioner.appointed_on) }}
                  </p>
                </div>
                <div v-if="practitioner.ceased_to_act_on">
                  <span>Ceased to act <br /></span>
                  <p class="fw-bolder">
                    {{ formatInsolvencyDate(practitioner.ceased_to_act_on) }}
                  </p>
                </div>
              </div>
            </div>
            <p></p>
          </div>
        </div>
        <hr v-if="companyInsolvency.cases.length > 1" />
      </div>
    </Card>
  </div>
</template>

<script>
import {
  Card,
  ConfirmDialog,
  SmartSelect,
  confirmType,
} from '@trustmark-2005-ltd/trustmark-shared-components';
import Datepicker from 'vue3-datepicker';
import SchemeBusinessFileUpload from './SchemeBusinessFileUpload.vue';
import { format } from 'date-fns';

export default {
  name: 'SchemeBusinessCeaseToTrade',
  components: {
    Card,
    ConfirmDialog,
    Datepicker,
    SmartSelect,
    SchemeBusinessFileUpload,
  },
  data() {
    return {
      errorMessage: null,
    };
  },
  computed: {
    currentAccount() {
      return this.$store.state.auth.account;
    },
    isCeaseToTradeEditor() {
      return this.currentAccount?.roles?.includes('CeaseToTradeEditor') ?? false;
    },
    model() {
      return this.$store.state.schemeBusiness?.business?.schemeBusiness;
    },
    companyInsolvency() {
      return this.$store.state.schemeBusiness?.companyInsolvency;
    },
    inputFormat() {
      return 'dd/MM/yyyy';
    },
    tradingStatusOptions() {
      return ['Dissolved', 'In Administration', 'Liquidation', 'Removed', 'Other'];
    },
  },
  watch: {
    model: {
      handler(newValue) {
        if (newValue?.ceaseToTradeDate) {
          newValue.ceaseToTradeDate = new Date(newValue.ceaseToTradeDate);
        }

        this.$store.dispatch('schemeBusiness/clearCompanyInsolvency', null);

        if (newValue?.registeredCompanyNumber) {
          this.$store.dispatch('schemeBusiness/getCompaniesHouseInsolvency', {
            registeredCompanyNumber: newValue.registeredCompanyNumber,
          });
        }
      },
      immediate: true,
    },
  },
  methods: {
    addFileToModel({ fileUrl, fileName }) {
      this.model.ceaseToTradeEvidences = this.model.ceaseToTradeEvidences || [];
      this.model.ceaseToTradeEvidences.push({
        fileUrl,
        fileName,
      });
    },
    updateExistingFile(index, { fileUrl, fileName }) {
      if (!this.model.ceaseToTradeEvidences || !this.model.ceaseToTradeEvidences[index]) {
        return;
      }
      if (fileUrl === '') {
        this.model.ceaseToTradeEvidences.splice(index, 1);
        return;
      }
      this.model.ceaseToTradeEvidences[index] = {
        fileUrl,
        fileName,
      };
    },
    saveCeaseToTrade() {
      if (!this.$route.params.schemeBusinessId || !this.$route.params.schemeId) {
        window.scrollTo(0, 0);
        return;
      }
      const payload = {
        schemeBusinessId: this.$route.params.schemeBusinessId,
        schemeId: this.$route.params.schemeId,
        ceaseToTradeDate: this.model.ceaseToTradeDate,
        tradingStatus: this.model.tradingStatus,
        ceaseToTradeEvidences: this.model.ceaseToTradeEvidences,
      };
      this.$refs.confirmCeaseToTrade
        .show({
          title: 'Cease to Trade',
          message: 'Are you sure you want to set this business to cease to trade?',
          confirmType: confirmType.standard,
          okButtonText: 'Cease to Trade',
        })
        .then((result) => {
          if (result === true) {
            this.$store
              .dispatch('schemeBusiness/updateCeaseToTrade', payload)
              .then(() => {
                window.scrollTo(0, 0);
                this.reloadSchemeBusiness();
              })
              .catch((err) => {
                window.scrollTo(0, 0);
                this.errorMessage =
                  (err.response && err.response.data && err.response.data.message) ||
                  err.message ||
                  err.toString();
              });
          }
        });
    },
    formatActionedDate(date) {
      if (!date) return '';
      const d = new Date(date);
      const day = d.getDate().toString().padStart(2, '0');
      const month = (d.getMonth() + 1).toString().padStart(2, '0');
      const year = d.getFullYear();
      return `${day}/${month}/${year}`;
    },
    enableBusiness() {
      if (!this.$route.params.schemeBusinessId || !this.$route.params.schemeId) {
        window.scrollTo(0, 0);
        return;
      }
      const payload = {
        schemeBusinessId: this.$route.params.schemeBusinessId,
        schemeId: this.$route.params.schemeId,
        ceaseToTradeDate: null,
        tradingStatus: null,
        ceaseToTradeEvidences: [],
      };
      this.$refs.confirmCeaseToTrade
        .show({
          title: 'Clear Cease to Trade',
          message: 'Are you sure you want to clear the cease to trade settings for this business?',
          confirmType: confirmType.standard,
          okButtonText: 'Clear Cease to Trade',
        })
        .then((result) => {
          if (result === true) {
            this.$store
              .dispatch('schemeBusiness/updateCeaseToTrade', payload)
              .then(() => {
                window.scrollTo(0, 0);
                this.reloadSchemeBusiness();
              })
              .catch((err) => {
                window.scrollTo(0, 0);
                this.errorMessage =
                  (err.response && err.response.data && err.response.data.message) ||
                  err.message ||
                  err.toString();
              });
          }
        });
    },
    reloadSchemeBusiness() {
      this.$store.dispatch('schemeBusiness/get', {
        schemeId: this.$route.params.schemeId,
        schemeBusinessId: this.$route.params.schemeBusinessId,
      });
    },
    formatInsolvencyType(type) {
      if (!type) return;
      if (type === 'corporate-voluntary-arrangement') {
        return 'Corporate voluntary arrangement (CVA)';
      }
      if (type === 'receiver-manager') {
        return 'Receiver/Manager appointed';
      }

      const text = type.replaceAll('-', ' ');
      return text.charAt(0).toUpperCase() + text.slice(1);
    },
    formatInsolvencyDate(date) {
      if (!date) return '';
      return format(new Date(date), 'dd MMMM yyyy');
    },
    formatAddress(address) {
      if (!address) return '-';
      let formattedAddress = '';

      if (address.address_line_1) {
        formattedAddress += address.address_line_1 + ',';
      }
      if (address.address_line_2) {
        formattedAddress += ' ' + address.address_line_2 + ',';
      }
      if (address.locality) {
        formattedAddress += ' ' + address.locality + ',';
      }
      if (address.region) {
        formattedAddress += ' ' + address.region + ',';
      }
      if (address.postal_code) {
        formattedAddress += ' ' + address.postal_code;
      }

      return formattedAddress;
    },
    formatDateLabel(type) {
      const labels = {
        'voluntary-arrangement-started-on': 'Date of meeting to approve CVA',
        'voluntary-arrangement-ended-on': 'Date of completion or termination of CVA',
        'instrumented-on': 'Instrument date',
        'administration-started-on': 'Administration started',
        'petitioned-on': 'Petitioned date',
        'wound-up-on': 'Commencement of winding up',
        'concluded-winding-up-on': 'Conclusion winding up',
        'dissolved-on': 'Dissolved on',
      };
      return labels[type];
    },
    mounted() {},
  },
};
</script>
